import { useState, useEffect, useMemo } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useMapStore } from '../store/mapStore';
export const CLUSTER_ZOOM_THRESHOLD = 14;
function roundTo4Decimals(n) {
    return Math.round(n * 10000) / 10000;
}
export function useViewportMarkers({ fetchFn, language, enabled = true, extraParams }) {
    const bounds = useMapStore((state) => state.bounds);
    const zoom = useMapStore((state) => state.zoom);
    // Debounced bounds and zoom
    const [debouncedBounds, setDebouncedBounds] = useState(bounds);
    const [debouncedZoom, setDebouncedZoom] = useState(zoom);
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedBounds(bounds);
            setDebouncedZoom(zoom);
        }, 300);
        return () => clearTimeout(timer);
    }, [bounds, zoom]);
    // Round bounds for cache key stability (~11m precision)
    const roundedBounds = useMemo(() => {
        if (!debouncedBounds)
            return null;
        return {
            north: roundTo4Decimals(debouncedBounds.getNorth()),
            south: roundTo4Decimals(debouncedBounds.getSouth()),
            east: roundTo4Decimals(debouncedBounds.getEast()),
            west: roundTo4Decimals(debouncedBounds.getWest()),
        };
    }, [debouncedBounds]);
    // Serialize bounds as "north,south,east,west" for the API query parameter
    const serializedBounds = useMemo(() => {
        if (!roundedBounds)
            return null;
        return `${roundedBounds.north},${roundedBounds.south},${roundedBounds.east},${roundedBounds.west}`;
    }, [roundedBounds]);
    const hasBounds = serializedBounds !== null;
    const query = useQuery({
        queryKey: ['markers', 'viewport', serializedBounds, debouncedZoom, language, extraParams],
        queryFn: () => fetchFn({
            bounds: serializedBounds,
            zoom: debouncedZoom,
            ...(language ? { language } : {}),
            ...(extraParams ?? {}),
        }),
        enabled: enabled && hasBounds,
        staleTime: 0,
        refetchInterval: 30000,
        placeholderData: keepPreviousData,
    });
    const markers = query.data?.markers ?? [];
    const clusters = query.data?.clusters ?? [];
    const totalCount = query.data?.totalCount ?? 0;
    return {
        markers,
        clusters,
        totalCount,
        isLoading: query.isLoading,
        error: query.error,
    };
}
